import type { RowValue } from "../../../../../../lib/csv";
import parseCSV, { getMissingFailuresMandatoryColumns } from "../../../../../../lib/csv";
import { isExpectedFormat } from "../../../../../../lib/fileHelper";
import { hasTimeFunctionDependency } from "diagnostics-lib/runtime";
import type { FileKeys, MapFile } from "./FilesType";
import {
  FileType,
  ErrorMessage,
  expectedFormats,
  expectedBindingFormat,
  CSVColumnMapping,
  CSVColumnMappingMandatory,
} from "./FilesType";

const iterationVariable = "iteration_variable";
const parameters = "parameters";

const pushFileToMap = (key: FileKeys, mapObject: Map<FileKeys, MapFile>, fileName: string) =>
  mapObject.get(key)?.fileName.push(fileName) ?? mapObject.set(key, { fileName: [fileName] });

function handleFiles(files: File[], fileType: FileKeys, validation: Map<FileKeys, MapFile>) {
  const foundFiles = findFiles(files, fileType);

  for (const filename of foundFiles) {
    pushFileToMap(fileType, validation, filename);
  }
}

const validateImport = async (
  files: File[],
  quantityNames: string[],
  modelType: string,
  expressionEngineInput: {
    corpus: Record<string, string | null>;
    graph: {
      [token: string]: string[] | null;
    } | null;
  },
): Promise<Map<FileKeys, MapFile>> => {
  const validation = new Map<FileKeys, MapFile>();

  if (modelType === FileType.dymola) {
    const mainFile = findDymosim(files);
    // this file is mandatory, error if not found
    if (mainFile.length > 0) {
      for (const filename of mainFile) {
        pushFileToMap("main", validation, filename);
      }
    }
    const dsin = findDsin(files);
    if (dsin.length > 0) {
      for (const filename of dsin) {
        pushFileToMap("variables", validation, filename);
      }
    }
  } else {
    const fmu = findFmu(files);
    if (fmu.length > 0) {
      for (const filename of fmu) {
        pushFileToMap("main", validation, filename);
      }
    }

    handleFiles(files, "variables", validation);
    handleFiles(files, "parameters", validation);
  }

  const bindingValidation = await validateBinding(files, quantityNames, expressionEngineInput);
  const bValidation = bindingValidation.get("binding");
  if (bValidation !== undefined) {
    validation.set("binding", bValidation);
  }

  const imageFile = findImageFile(files);
  if (imageFile.length > 0) {
    for (const filename of imageFile) {
      pushFileToMap("image", validation, filename);
    }
  }

  findUnexpectedFiles(modelType, files).map(filename =>
    pushFileToMap("nonExpected", validation, filename),
  );

  // add to nonExpected list every file that are remaining
  for (const file of files) {
    let fileFound = false;
    for (const e of validation) {
      const filename = e[1].fileName;
      if (filename.find(f => f === file.name)) {
        fileFound = true;
      }
    }
    if (!fileFound) pushFileToMap("nonExpected", validation, file.name);
  }

  return validation;
};

function getMissingMandatoryColumns(content: string) {
  return getMissingFailuresMandatoryColumns(content, CSVColumnMappingMandatory);
}

export function isAscendingArrayStartingWithOne(arr: number[]): boolean {
  const arrSorted = arr.sort((a, b) => a - b);
  if (arrSorted.length === 0) return true;
  const startWithOne = arrSorted[0] === 1;
  const isAscendingOrder = arrSorted.every((value, index) => {
    const orderPrevious = arrSorted[index - 1];
    return index === 0 || (orderPrevious && value === orderPrevious + 1);
  });
  return startWithOne && isAscendingOrder;
}

/**
 *
 * @param files list of file to check
 * @param quantityNames quantity of the project
 * @param isOutput default false, specifiy if this is an output file and require more check
 * @returns validation Map<FileKeys, MapFile>
 */
export async function validateBinding(
  files: File[],
  quantityNames: string[],
  expressionEngineInput: {
    corpus: Record<string, string | null>;
    graph: {
      [token: string]: string[] | null;
    } | null;
  },
) {
  const validation = new Map<FileKeys, MapFile>();
  const bindingFile = await findBindingFile(files);
  if (bindingFile.length > 0) {
    // we only want error to be computed for the first file since we only send the first one
    let firstFile = true;
    for (const { file, content } of bindingFile) {
      pushFileToMap("binding", validation, file.name);
      const fileContent = await file.text();
      const missingMandatoryColumns = getMissingMandatoryColumns(fileContent);
      const bindingValidation = validation.get("binding");
      if (firstFile) {
        // bad header in csv binding file
        if (content === undefined) {
          if (bindingValidation) {
            bindingValidation.error = [
              {
                message: `${ErrorMessage.missingColumn} in ${file.name}, expected columns: `,
                data: Object.values(expectedBindingFormat),
              },
            ];
          }
        } else if (missingMandatoryColumns.length > 0) {
          if (bindingValidation) {
            bindingValidation.error = [
              {
                message: `${ErrorMessage.missingColumn} in ${file.name}, expected columns: `,
                data: missingMandatoryColumns,
              },
            ];
          }
        } else {
          const bindingValidation = validation.get("binding");
          if (bindingValidation) {
            const unknownQuantities = content
              .map(e => e["name"])
              .filter(e => typeof e === "string" && !quantityNames.includes(e)) as string[];
            if (unknownQuantities.length > 0) {
              bindingValidation.error = [
                {
                  message: ErrorMessage.unknownQuantity,
                  data: unknownQuantities,
                },
              ];
            }

            const filteredOrderExternalGap = content.filter(
              e =>
                e["outputModelName"] === null &&
                e["inputModelName"] !== null &&
                e["orderExternalGap"] !== null &&
                Number.isInteger(Number(e["orderExternalGap"])),
            );
            const invalidOrderExternalAscendingArrayStartingWithOne =
              isAscendingArrayStartingWithOne(
                filteredOrderExternalGap.map(e => Number(e["orderExternalGap"])),
              );
            if (!invalidOrderExternalAscendingArrayStartingWithOne) {
              bindingValidation.error = [
                {
                  message: ErrorMessage.invalidOrderExternalAscendingArrayStartingWithOne,
                  data: filteredOrderExternalGap.flatMap(e => e["orderExternalGap"] || []),
                },
              ];
            }

            const invalidOrderExternalGap = content.filter(e => {
              if (
                e["orderExternalGap"] !== null &&
                !Number.isInteger(Number(e["orderExternalGap"]))
              ) {
                return true;
              }
              return false;
            });
            if (invalidOrderExternalGap.length > 0) {
              bindingValidation.error = [
                {
                  message: ErrorMessage.invalidOrderExternalGap,
                  data: invalidOrderExternalGap.flatMap(e => e["name"] || []),
                },
              ];
            }

            const invalidReferenceValue = content.filter(e => {
              if (e["referenceValue"] !== null && !Number.isFinite(Number(e["referenceValue"]))) {
                return true;
              }
              return false;
            });
            if (invalidReferenceValue.length > 0) {
              bindingValidation.error = [
                {
                  message: ErrorMessage.invalidReferenceValue,
                  data: invalidReferenceValue.flatMap(e => e["name"] || []),
                },
              ];
            }

            const invalidExternalGap = content.filter(e => {
              if (e["orderExternalGap"] !== null && e["referenceValue"] === null) {
                return true;
              }
              return false;
            });
            if (invalidExternalGap.length > 0) {
              bindingValidation.error = [
                {
                  message: ErrorMessage.invalidExternalGap,
                  data: invalidExternalGap.flatMap(e => e["name"] || []),
                },
              ];
            }

            // check that offsetExpected are numbers
            const invalidOffset = content
              .map(e => e["offset"])
              .filter(e => e !== undefined && e !== null && e !== "")
              .filter(e => isNaN(Number(e))) as string[];
            if (invalidOffset.length > 0) {
              bindingValidation.error = [
                {
                  message: ErrorMessage.offsetExpectedNotANumber,
                  data: invalidOffset,
                },
              ];
            }

            /**
             * @description
             * 24/12/2025
             * Edge case:
             * If WASM is disabled in the target browser, expressionEngineInput.graph is null.
             * WASM initialization must be decoupled from the expression engine;
             * otherwise, the time function check in setup is always incorrect.
             */
            if (expressionEngineInput.graph) {
              const hasFoundTimeFunction = content
                .flatMap(e =>
                  (e["inputModelName"] === null || e["inputModelName"] === undefined) &&
                  (e["name"] === null || e["name"] === undefined)
                    ? []
                    : [e["name"]],
                )
                .some(
                  inputName =>
                    expressionEngineInput.graph &&
                    inputName !== null &&
                    inputName !== undefined &&
                    hasTimeFunctionDependency(
                      [inputName],
                      expressionEngineInput.corpus,
                      expressionEngineInput.graph,
                    ),
                );
              if (hasFoundTimeFunction) {
                bindingValidation.error = [
                  {
                    message: ErrorMessage.CannotDependOnPastFunction,
                    data: [],
                  },
                ];
              }
            }
          }
        }
      }
      firstFile = false;
    }
  }

  return validation;
}

function findUnexpectedFiles(modelType: string, files: File[]): string[] {
  const unexpectedFiles: string[] = [];
  for (const file of files) {
    // check non expected file format
    if (!isExpectedFormat(file, ["png", "svg"])) {
      if (modelType === FileType.dymola) {
        if (
          isExpectedFormat(file, ["fmu"]) ||
          (file.name.includes(iterationVariable) && isExpectedFormat(file, ["csv"]))
        )
          unexpectedFiles.push(file.name);
      } else if (!isExpectedFormat(file, ["csv"])) {
        if (
          !isExpectedFormat(file, expectedFormats) &&
          (file.name === "dsin.txt" || file.name === "dymosim")
        )
          unexpectedFiles.push(file.name);
      }
    }
  }
  return unexpectedFiles;
}

function findImageFile(files: File[]): string[] {
  const filesFound: string[] = [];
  for (const file of files) {
    if (isExpectedFormat(file, ["svg"])) {
      filesFound.push(file.name);
    }
  }
  return filesFound;
}

async function findBindingFile(
  files: File[],
): Promise<{ file: File; content: RowValue[] | undefined }[]> {
  const filesFound: { file: File; content: RowValue[] | undefined }[] = [];
  for (const file of files) {
    // csv and NOT iteration_variable
    if (
      isExpectedFormat(file, ["csv"]) &&
      !file.name.includes(iterationVariable) &&
      !file.name.includes(parameters)
    ) {
      const csv = await file.text();
      const csvParsed = parseCSV(csv, "line", CSVColumnMapping);
      filesFound.push({ file, content: csvParsed });
    }
  }
  return filesFound;
}

function findFiles(files: File[], fileName: string): string[] {
  const filesFound = [];
  for (const file of files) {
    if (isExpectedFormat(file, ["csv"]) && file.name.includes(fileName)) filesFound.push(file.name);
  }
  return filesFound;
}

function findFmu(files: File[]): string[] {
  return files.filter(f => f.name.includes(".fmu")).map(f => f.name);
}

function findDsin(files: File[]): string[] {
  return files.filter(f => f.name === "dsin.txt").map(f => f.name);
}

function findDymosim(files: File[]): string[] {
  return files.filter(f => f.name === "dymosim").map(f => f.name);
}

export default validateImport;
